import { useRef, useState } from 'react'
import type ReactECharts from 'echarts-for-react'
import { useExportAssetsCsv } from '../../../hooks/useExportAssetsCsv'
import { Download } from 'lucide-react'
import { Button } from '@src/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import RiskProfileLossCurveChart, {
  type LossCurveMode,
} from '../../../chart/RiskProfileLossCurve.chart'
import { useProfileAllAssetsData } from '../../../hooks/useProfileAssetsRequest'
import { useRiskProfileRequest } from '../../../hooks/useRiskProfileRequest'
import { useExportChartImage } from '../../../hooks/useExportChartImage'
import type { RiskProfileQueryParams } from '../../../hooks/useRiskProfileQueryParams'
import type { RiskProfileMetricOption } from '../../../types/riskProfile.types'
import { ChartQueryState } from '../../ChartQueryState'
import { InterpretPanel, InterpretToggle } from '../../InterpretToggle'
import { RiskProfileCard } from '../../RiskProfileCard'

export interface RiskProfileLossCurveWidgetProps {
  queryParams: RiskProfileQueryParams | null
  metricOption: RiskProfileMetricOption
}

export function RiskProfileLossCurveWidget({
  queryParams,
  metricOption,
}: RiskProfileLossCurveWidgetProps) {
  const [showInterpret, setShowInterpret] = useState(false)
  const assetsQuery = useProfileAllAssetsData(queryParams)
  const exportRequest = useRiskProfileRequest({ queryParams, widget: 'riskByElementType' })
  const chartRef = useRef<ReactECharts>(null)
  const handleExportImage = useExportChartImage(chartRef, 'loss-concentration-curve')
  const { exportCsv, isExporting } = useExportAssetsCsv(exportRequest)

  const mode: LossCurveMode = metricOption.metric === 'quantity' ? 'quantity' : 'damage_cost'

  return (
    <RiskProfileCard
      title="Loss concentration curve"
      description="Lorenz-style curve showing how cumulative damage is distributed across cumulative share of assets."
      onExport={
        <div className="flex items-center gap-2">
          <InterpretToggle active={showInterpret} onClick={() => setShowInterpret((v) => !v)} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 gap-1.5 px-2 text-xs"
              >
                Export
                <Download className="size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportImage}>Export as Image</DropdownMenuItem>
              <DropdownMenuItem onClick={exportCsv} disabled={isExporting || !exportRequest}>
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
      className="min-h-96"
    >
      {showInterpret && (
        <InterpretPanel>
          <p>Lorenz-style concentration curve.</p>
          <p>
            Steep rise on left = small share of assets driving most loss → focus on individual asset
            upgrades.
          </p>
          <p>Flatter line = damage spread → system-wide measures.</p>
          <p>Hover any point → asset name and cumulative percentages.</p>
        </InterpretPanel>
      )}
      <ChartQueryState query={assetsQuery}>
        {(data) => <RiskProfileLossCurveChart ref={chartRef} data={data} mode={mode} />}
      </ChartQueryState>
    </RiskProfileCard>
  )
}
